import { useCallback, useEffect, useRef, useState } from "react";

import { useSearchParams } from "react-router-dom";

import { LIVE_LEDGER_PAGE_QUERY_KEY } from "../constants";

const parsePage = (raw: string | null): number => {
  if (raw === null) return 1;
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
};

export interface UsePageParamResult {
  page: number;
  setPage: (nextPage: number) => void;
}

export const usePageParam = (): UsePageParamResult => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPage = parsePage(searchParams.get(LIVE_LEDGER_PAGE_QUERY_KEY));
  const [page, rawSetPage] = useState(urlPage);
  const hasLocalUpdate = useRef(false);

  // Runs before the URL write so a flag left by a no-op setPage cannot
  // override an external navigation.
  useEffect(() => {
    hasLocalUpdate.current = false;
    rawSetPage(urlPage);
  }, [urlPage]);

  useEffect(() => {
    if (!hasLocalUpdate.current) return;
    const target = page <= 1 ? null : String(page);
    if (target === searchParams.get(LIVE_LEDGER_PAGE_QUERY_KEY)) return;
    hasLocalUpdate.current = false;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (target === null) {
          next.delete(LIVE_LEDGER_PAGE_QUERY_KEY);
        } else {
          next.set(LIVE_LEDGER_PAGE_QUERY_KEY, target);
        }
        return next;
      },
      { replace: true }
    );
  }, [page, searchParams, setSearchParams]);

  const setPage = useCallback((nextPage: number) => {
    hasLocalUpdate.current = true;
    rawSetPage(nextPage);
  }, []);

  return { page, setPage };
};
